import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './api-error.js';
import { REQUEST_TIMEOUT_MS } from './api.js';

/**
 * WEB-004.
 *
 * The only judgement here is what not to retry. A 401 means the session is gone
 * and a 403 means this caller may not have the thing; repeating either three
 * times delays the redirect the user actually needs and triples the load while
 * doing it. A 422 is the request being wrong, which retrying cannot change.
 *
 * Everything else - a 5xx, a dropped connection, a Lambda cold start - is worth
 * one more attempt.
 */
export function retryableStatus(error: unknown): boolean {
  // A cancel is a decision, not a failure. Anything that is not an `ApiError`
  // is retried by default - a dropped connection should be - but an abort came
  // from us, and retrying it three times with backoff turns "stop" into three
  // more requests and three seconds. Named rather than matched on `instanceof
  // DOMException`, because jsdom and browsers disagree about the class while
  // both set the name.
  if (isAbort(error)) return false;
  if (!(error instanceof ApiError)) return true;
  return error.status >= 500;
}

function isAbort(error: unknown): boolean {
  // Duck-typed on purpose, and the comment above understated it: `DOMException`
  // is not `instanceof Error` in jsdom at all, so an `instanceof` guard here
  // passed its unit test in one environment and would have been wrong in the
  // other. Measured - this function returned `false` for a real `AbortError`.
  return typeof error === 'object' && error !== null && 'name' in error
    ? (error as { name: unknown }).name === 'AbortError'
    : false;
}

/**
 * What a person waits, worst case, before a failure is shown - COW-488.
 *
 * Three attempts at `REQUEST_TIMEOUT_MS` each, plus TanStack's default backoff
 * of 1 s and 2 s between them: **about 39 seconds**. That is the bound the
 * ticket asks to be stated, and it is a long time to look at a spinner, which
 * is why `AppShell` says which attempt it is on rather than repeating one word.
 *
 * It is finite, which is the whole point. Before this it was not: a request
 * that never settled left the query `pending` with nothing scheduled to end it,
 * and the measured symptom was 107 seconds and counting.
 */
export const WORST_CASE_WAIT_MS = 3 * REQUEST_TIMEOUT_MS + 1_000 + 2_000;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Three attempts total. `WORST_CASE_WAIT_MS` above is derived from this
        // number, so changing it changes the bound.
        retry: (failureCount, error) => failureCount < 2 && retryableStatus(error),
        // The API is a network hop behind a JWT authorizer; refetching on every
        // window focus turns a tab switch into a burst of authorized requests.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
      mutations: { retry: false },
    },
  });
}
